/**
 * operationalLayersStore — single-fetch cache for `geoServer.getAppLayers`.
 *
 * The result drives both the per-app system loaders and the app-availability
 * check used by the root apps page and SafeSystem menu (keyed by `dataset`).
 *
 * Lifecycle: the root layout calls `load(apiKey)` whenever `apiKey` changes.
 * `load` dedupes in-flight calls by api-key and short-circuits on success. A
 * fresh key triggers a refetch; `clear()` resets state when the key is removed.
 */

import type { TLayer as TOperationalFeature } from '@safesystem/geoserver-api';
import { geoServer } from '#lib/config.js';
import { apiKeyStore } from '#lib/stores/apiKeyStore.svelte.js';
import { FetchStatus } from '#lib/types.js';
import { getLogger } from '@logtape/logtape';

const log = getLogger(['platform', 'operational-layers-store']);

function createOperationalLayersStore() {
	let layers = $state<TOperationalFeature[]>([]);
	let status = $state<FetchStatus>(FetchStatus.IDLE);
	let error = $state<string | null>(null);
	// Tracks the api-key the cached `layers` belong to so a key change forces
	// a refetch even if the previous load is still in flight.
	let loadedKey: string | null = null;
	let inFlight: Promise<void> | null = null;

	const datasets = $derived(new Set(layers.map((l) => l.dataset)));

	return {
		get layers() {
			return layers;
		},
		get status() {
			return status;
		},
		get error() {
			return error;
		},
		get datasets() {
			return datasets;
		},

		/**
		 * Fetch operational layers for `apiKey`. Idempotent for the same key
		 * once successful; in-flight calls share a single promise.
		 */
		async load(apiKey: string): Promise<void> {
			if (status === FetchStatus.SUCCESS && loadedKey === apiKey) return;
			if (inFlight && loadedKey === apiKey) return inFlight;

			const cred = apiKeyStore.credentials;
			if (!cred) {
				log.error('load: no credentials — validate the api key first');
				return;
			}

			loadedKey = apiKey;
			status = FetchStatus.LOADING;
			error = null;

			inFlight = (async () => {
				try {
					const result = (await geoServer.getAppLayers(apiKey, cred.namespace)) ?? [];
					// Guard against an interleaved key change between request and
					// response — discard a stale response.
					if (loadedKey !== apiKey) return;
					layers = result;
					status = FetchStatus.SUCCESS;
					log.info('Loaded {layerCount} layers across {datasetCount} datasets', {
						layerCount: result.length,
						datasetCount: new Set(result.map((l) => l.dataset)).size
					});
				} catch (err) {
					if (loadedKey !== apiKey) return;
					layers = [];
					status = FetchStatus.FAILURE;
					error = err instanceof Error ? err.message : String(err);
					log.error('Load error: {error}', { error });
				} finally {
					inFlight = null;
				}
			})();

			return inFlight;
		},

		/** Reset state — call when the api key is cleared. */
		clear() {
			layers = [];
			status = FetchStatus.IDLE;
			error = null;
			loadedKey = null;
			inFlight = null;
		}
	};
}

export const operationalLayersStore = createOperationalLayersStore();
